import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  InputAdornment,
  Box,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';

const TradingSettings = ({ open, onClose, onSave }) => {
  const [selectedMarket, setSelectedMarket] = useState('A股'); // 默认选择A股
  const [commissionRate, setCommissionRate] = useState(0.03); // 佣金率
  const [stampDutyRate, setStampDutyRate] = useState(0.1); // 印花税率
  const [platformFee, setPlatformFee] = useState(0); // 平台费（美股专用）
  const [minFee, setMinFee] = useState(0); // 最低费用（美股专用）
  
  // 市场费率配置
  const marketConfigs = {
    'A股': {
      commissionRate: 0.03,
      stampDutyRate: 0.1,
      platformFee: 0,
      minFee: 0,
      description: 'A股市场费率'
    },
    '港股': {
      commissionRate: 0.03,
      stampDutyRate: 0.1,
      platformFee: 0,
      minFee: 0,
      description: '港股市场费率'
    },
    '美股': {
      commissionRate: 0,
      stampDutyRate: 0,
      platformFee: 0.01, // 0.01美元/股
      minFee: 2, // 最低2美元/笔
      description: '美股市场费率'
    }
  };

  // 从localStorage加载设置
  useEffect(() => {
    const savedMarket = localStorage.getItem('tradingMarket');
    const savedCommissionRate = localStorage.getItem('tradingCommissionRate');
    const savedStampDutyRate = localStorage.getItem('tradingStampDutyRate');
    const savedPlatformFee = localStorage.getItem('tradingPlatformFee');
    const savedMinFee = localStorage.getItem('tradingMinFee');
    
    if (savedMarket) {
      setSelectedMarket(savedMarket);
    }
    if (savedCommissionRate) {
      setCommissionRate(parseFloat(savedCommissionRate));
    }
    if (savedStampDutyRate) {
      setStampDutyRate(parseFloat(savedStampDutyRate));
    }
    if (savedPlatformFee) {
      setPlatformFee(parseFloat(savedPlatformFee));
    }
    if (savedMinFee) {
      setMinFee(parseFloat(savedMinFee));
    }
  }, []);

  const handleSave = () => {
    // 保存到localStorage
    localStorage.setItem('tradingMarket', selectedMarket);
    localStorage.setItem('tradingCommissionRate', commissionRate.toString());
    localStorage.setItem('tradingStampDutyRate', stampDutyRate.toString());
    localStorage.setItem('tradingPlatformFee', platformFee.toString());
    localStorage.setItem('tradingMinFee', minFee.toString());
    
    // 调用父组件的保存回调
    if (onSave) {
      onSave({
        market: selectedMarket,
        commissionRate,
        stampDutyRate,
        platformFee,
        minFee
      });
    }
    
    onClose();
  };

  const handleReset = () => {
    const config = marketConfigs[selectedMarket];
    setCommissionRate(config.commissionRate);
    setStampDutyRate(config.stampDutyRate);
    setPlatformFee(config.platformFee);
    setMinFee(config.minFee);
  };
  
  // 市场切换时更新费率
  const handleMarketChange = (market) => {
    setSelectedMarket(market);
    const config = marketConfigs[market];
    setCommissionRate(config.commissionRate);
    setStampDutyRate(config.stampDutyRate);
    setPlatformFee(config.platformFee);
    setMinFee(config.minFee);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <SettingsIcon sx={{ mr: 1 }} />
        交易费率设置
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            选择交易市场并设置相应的费率，这些费率将用于所有计算器的费用计算。
          </Typography>
          
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            {/* 市场选择 */}
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>交易市场</InputLabel>
                <Select
                  value={selectedMarket}
                  label="交易市场"
                  onChange={(e) => handleMarketChange(e.target.value)}
                >
                  <MenuItem value="A股">A股</MenuItem>
                  <MenuItem value="港股">港股</MenuItem>
                  <MenuItem value="美股">美股</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* A股和港股的费率设置 */}
            {(selectedMarket === 'A股' || selectedMarket === '港股') && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="佣金率"
                    type="number"
                    fullWidth
                    value={commissionRate}
                    onChange={(e) => setCommissionRate(parseFloat(e.target.value) || 0)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    inputProps={{
                      step: 0.001,
                      min: 0,
                      max: 1
                    }}
                    helperText={selectedMarket === 'A股' ? "买卖双向收取，通常为0.02%-0.03%" : "买卖双向收取，港股为0.03%"}
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="印花税率"
                    type="number"
                    fullWidth
                    value={stampDutyRate}
                    onChange={(e) => setStampDutyRate(parseFloat(e.target.value) || 0)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    inputProps={{
                      step: 0.001,
                      min: 0,
                      max: 1
                    }}
                    helperText={selectedMarket === 'A股' ? "仅卖出时收取，目前为0.1%" : "买卖双向收取，港股为0.1%"}
                    variant="outlined"
                  />
                </Grid>
              </>
            )}
            
            {/* 美股的费率设置 */}
            {selectedMarket === '美股' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="佣金+平台费"
                    type="number"
                    fullWidth
                    value={platformFee}
                    onChange={(e) => setPlatformFee(parseFloat(e.target.value) || 0)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">美元/股</InputAdornment>,
                    }}
                    inputProps={{
                      step: 0.001,
                      min: 0
                    }}
                    helperText="每股收费，通常为0.01美元/股"
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="最低费用"
                    type="number"
                    fullWidth
                    value={minFee}
                    onChange={(e) => setMinFee(parseFloat(e.target.value) || 0)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">美元/笔</InputAdornment>,
                    }}
                    inputProps={{
                      step: 0.01,
                      min: 0
                    }}
                    helperText="每笔交易最低收费，通常为2美元"
                    variant="outlined"
                  />
                </Grid>
              </>
            )}
          </Grid>
          
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              {selectedMarket} 费用计算说明：
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedMarket === 'A股' && (
                <>
                  • 佣金：买入和卖出时都会收取，按成交金额的百分比计算<br/>
                  • 印花税：仅在卖出时收取，按成交金额的百分比计算<br/>
                  • 总费用 = 佣金 + 印花税（如适用）
                </>
              )}
              {selectedMarket === '港股' && (
                <>
                  • 佣金：买入和卖出时都会收取，按成交金额的百分比计算<br/>
                  • 印花税：买入和卖出时都会收取，按成交金额的百分比计算<br/>
                  • 总费用 = 佣金 + 印花税
                </>
              )}
              {selectedMarket === '美股' && (
                <>
                  • 佣金+平台费：按每股固定金额收取<br/>
                  • 最低费用：每笔交易的最低收费标准<br/>
                  • 总费用 = max(股数 × 每股费用, 最低费用)
                </>
              )}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleReset} color="secondary">
          恢复默认
        </Button>
        <Button onClick={onClose}>
          取消
        </Button>
        <Button onClick={handleSave} variant="contained">
          保存设置
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TradingSettings;